/**
 * Build a `BetLineContext` from a `GameEventRow` + the currently-selected
 * spread or total line — used by SpreadsTotals to drive the right-side
 * GameDetailPanel's alternate header (bet-description title + chip strip)
 * for synthesized spread/total bets.
 *
 * The line magnitude in the title is highlighted in green to match the
 * dashboard table's chip-strip description style, so the user sees the
 * same phrasing in both places.
 */

import type { ReactNode } from 'react';
import type { GameEventRow, MarketSide, Sport, SpreadLine, TotalLine } from '../ValueDashboard/types';
import type { BetLineContext } from '../ValueDashboard/GameDetailPanel';
import {
  linePriceCents,
  spreadLineKey,
  synthesizeFromSpread,
  synthesizeFromTotal,
  totalLineKey,
} from './synthesize';

function sportUnit(sport: Sport): string {
  switch (sport) {
    case 'mlb':
      return 'runs';
    case 'nhl':
      return 'goals';
    case 'atp':
    case 'wta':
      return 'games';
    case 'ufc':
      return 'rounds';
    case 'nba':
    case 'wnba':
    case 'cbb':
    default:
      return 'points';
  }
}

function formatSignedLine(signed: number): string {
  const sign = signed < 0 ? '-' : '+';
  return `${sign}${Math.abs(signed)}`;
}

interface SpreadArgs {
  kind: 'spread';
  event: GameEventRow;
  lines: SpreadLine[];
  selectedIdx: number;
  onSelectIdx: (idx: number) => void;
}

interface TotalArgs {
  kind: 'total';
  event: GameEventRow;
  lines: TotalLine[];
  selectedIdx: number;
  onSelectIdx: (idx: number) => void;
}

export function buildBetLineContext(args: SpreadArgs | TotalArgs): BetLineContext {
  const unit = sportUnit(args.event.sport);

  if (args.kind === 'spread') {
    const titleFor = (_side: MarketSide, idx: number): ReactNode => {
      const line = args.lines[idx];
      const mag = line ? Math.abs(line.line) : 0;
      // Title describes the market (favored team's YES outcome), not the
      // bet direction. The YES/NO toggle in OrderEntryForm controls which
      // side the user is buying — it doesn't change what the market is.
      // 3-letter team code matches the dashboard's bet-description.
      const favoredCode = line
        ? line.favored === 'away'
          ? args.event.awayCode
          : args.event.homeCode
        : args.event.awayCode;
      return (
        <span>
          {favoredCode} wins by over{' '}
          <span className="text-positive">{mag}</span> {unit}
        </span>
      );
    };
    return {
      titleFor,
      chips: args.lines.map((sp) => ({
        key: spreadLineKey(sp),
        label: formatSignedLine(sp.line),
        priceCents: linePriceCents(sp),
      })),
      selectedIdx: args.selectedIdx,
      onSelectIdx: args.onSelectIdx,
      getGameForIdx: (idx) => {
        const line = args.lines[idx];
        return line ? synthesizeFromSpread(args.event, line) : null;
      },
    };
  }

  const titleFor = (_side: MarketSide, idx: number): ReactNode => {
    const line = args.lines[idx];
    const mag = line ? line.line : 0;
    // Title describes the market (Over X.5) — symmetric with spread,
    // matches the dashboard's "Over X scored" chip-strip description.
    // The YES/NO toggle selects the bet direction below.
    return (
      <span>
        {args.event.awayCode} @ {args.event.homeCode} Total more than{' '}
        <span className="text-positive">{mag}</span> {unit}
      </span>
    );
  };
  return {
    titleFor,
    chips: args.lines.map((t) => ({
      key: totalLineKey(t),
      label: t.line.toString(),
      priceCents: linePriceCents(t),
    })),
    selectedIdx: args.selectedIdx,
    onSelectIdx: args.onSelectIdx,
    getGameForIdx: (idx) => {
      const line = args.lines[idx];
      return line ? synthesizeFromTotal(args.event, line) : null;
    },
  };
}
